// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MetricCard
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS metric card for a single computed summary
 * metric. The card formats percentages and raw counts while exposing trend
 * coloring from the shared analytics tokens.
 */

import type { MetricSummary } from "@geenius/analytics";
import { Badge, Card, CardContent, Skeleton } from "@geenius/ui/react-css";
import type React from "react";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS metric card.
 *
 * @property metric Metric summary payload rendered by the card.
 * @property isLoading Whether the card should render placeholder content.
 * @property className Optional class name merged into the card wrapper.
 */
export interface MetricCardProps {
  metric: MetricSummary;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React CSS metric card for one summary metric.
 *
 * @param props Summary metric payload for the card.
 * @returns A metric card element with formatted value and optional trend.
 */
export function MetricCard(props: MetricCardProps): React.ReactElement {
  const { metric } = props;
  if (props.error) {
    return (
      <Card className={props.className}>
        <CardContent className="gn-analytics-metric">
          <ErrorState
            error={props.error}
            title={metric.label}
            onRetry={props.onRetry}
          />
        </CardContent>
      </Card>
    );
  }

  if (props.isLoading) {
    return (
      <Card className={props.className}>
        <CardContent className="gn-analytics-metric">
          <div
            className="gn-analytics-metric__loading"
            data-analytics-metric-loading
            aria-busy="true"
          >
            <span className="gn-analytics-a11y__sr-only">{metric.label}</span>
            <Skeleton height={14} />
            <Skeleton height={32} />
            <Skeleton height={20} />
          </div>
        </CardContent>
      </Card>
    );
  }

  const formattedValue =
    metric.format === "percent"
      ? `${metric.value.toFixed(1)}%`
      : metric.format === "duration"
        ? `${(metric.value / 1000).toFixed(1)}s`
        : metric.value.toLocaleString();
  const formattedChange =
    metric.changePercent !== undefined
      ? `${metric.changePercent.toFixed(1)}%`
      : metric.change !== undefined
        ? metric.change.toLocaleString()
        : null;
  const trendClassName = [
    "gn-analytics-metric__change",
    `gn-analytics-metric__change--${metric.trend}`,
  ].join(" ");

  return (
    <Card className={props.className}>
      <CardContent className="gn-analytics-metric">
        <span className="gn-analytics-metric__label">{metric.label}</span>
        <span className="gn-analytics-metric__value">{formattedValue}</span>
        {formattedChange !== null && (
          <Badge
            variant={
              metric.trend === "up"
                ? "success"
                : metric.trend === "down"
                  ? "danger"
                  : "secondary"
            }
            size="sm"
          >
            <span className={trendClassName} data-trend={metric.trend}>
              {metric.trend === "up"
                ? "↑"
                : metric.trend === "down"
                  ? "↓"
                  : "→"}{" "}
              {formattedChange}
            </span>
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
